import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { deleteWidgetMutation } from '@graphql/dashboard';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { OverlayService } from '@services/ui/overlay.service';
import { ToastService } from '@services/ui/toast.service';
import { LOADING } from '@utils/loader';
import { Apollo } from 'apollo-angular';
import {
  DeleteWidgetMutation,
  DeleteWidgetMutationVariables,
} from 'gql/graphql';
import { DashboardSidePanelComponent } from '../dashboard-side-panel/dashboard-side-panel.component';
import { COLS, DashboardSidePanelData } from '@constants/widgets';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { t } from '@utils/strings';
import { listenForEventsWhen } from '@utils/angular-ext';

@Component({
  selector: 'abb-dashboard-widget',
  standalone: true,
  imports: [
    DragDropModule,
    CommonModule,
    AbbIconComponent,
    AbbButtonComponent,
    OverflowTooltipDirective,
  ],
  template: `
    <div
      class="bg-abb-background p-4 rounded absolute"
      [ngStyle]="{
        top: currentTop() + 'px',
        left: currentLeft() + 'px',
        width: currentWidth() + 'px',
        height: currentHeight() + 'px',
        minWidth: minWidth + 'px',
        minHeight: minHeight + 'px'
      }"
      [ngClass]="{
        'pointer-events-none': dragInProgress(),
        'z-20': dragging() || resizing(),
        'z-10': !dragging() && !resizing(),
        'shadow-lg': editMode(),
        'shadow-md': !editMode(),
        'select-none': editMode()
      }"
      cdkDrag
      cdkDragBoundary=".boundary"
      (cdkDragStarted)="onDragStart()"
      (cdkDragReleased)="onDragEnd()"
      cdkDragPreviewClass="pointer-events-none"
      [cdkDragFreeDragPosition]="dragPosition"
      [cdkDragDisabled]="!editMode() || resizing()"
      #ref
    >
      @if (editMode()) {
      <div
        class="bg-abb-primary w-2 h-2 absolute -right-1 -bottom-1 rounded-sm cursor-se-resize hover:bg-abb-primary-light transition-colors"
        (pointerdown)="onResizeStart($event, 'bottom-right')"
      ></div>
      <div
        class="bg-abb-primary w-2 h-2 absolute -left-1 -bottom-1 rounded-sm cursor-sw-resize hover:bg-abb-primary-light transition-colors"
        (pointerdown)="onResizeStart($event, 'bottom-left')"
      ></div>
      <div
        class="bg-abb-primary w-2 h-2 absolute -right-1 -top-1 rounded-sm cursor-ne-resize hover:bg-abb-primary-light transition-colors"
        (pointerdown)="onResizeStart($event, 'top-right')"
      ></div>
      <div
        class="bg-abb-primary w-2 h-2 absolute -left-1 -top-1 rounded-sm cursor-nw-resize hover:bg-abb-primary-light transition-colors"
        (pointerdown)="onResizeStart($event, 'top-left')"
      ></div>
      }
      <div class="relative flex flex-col h-full w-full">
        @if (editMode()) {
        <div cdkDragHandle class="absolute -left-2 top-0">
          <abb-icon
            size="medium"
            class="cursor-move"
            name="mat/drag_indicator"
          ></abb-icon>
        </div>
        <div class="absolute right-0 top-0 flex gap-x-2">
          <abb-button
            type="button"
            (onclick)="cloneWidget()"
            variant="none"
            class="hover:drop-shadow-lg active:text-abb-font/[0.78] hover:text-abb-font/[0.78] transition-all"
            [icon]="'mat/content_copy'"
          />
          <abb-button
            type="button"
            (onclick)="editWidget()"
            variant="none"
            class="hover:drop-shadow-lg active:text-abb-font/[0.78] hover:text-abb-font/[0.78] transition-all"
            [icon]="'mat/settings'"
          />
          <abb-button
            type="button"
            (onclick)="deleteWidget()"
            variant="none"
            class="hover:drop-shadow-lg active:text-abb-font/[0.78] hover:text-abb-font/[0.78] transition-all"
            [icon]="'mat/delete'"
          />
        </div>
        }
        <div
          class="font-bold h-8 truncate pr-[5rem]"
          [ngClass]="{ 'pl-5': editMode() }"
          overflowTooltip
          [useOffsetWidth]="true"
        >
          {{ name() }}
        </div>
        <div class="overflow-auto" style="height: calc(100% - 32px);">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
  `,
  styles: ``,
})
export class DashboardWidgetComponent {
  minWidth = 200;
  minHeight = 100;
  ref = viewChild<ElementRef<HTMLDivElement>>('ref');
  dashboardId = input.required<string>();
  widgets = input.required<DashboardSidePanelData['widgets']>();
  name = input.required<string>();
  editMode = input.required<boolean>();
  cellHeight = input.required<number>();
  cellWidth = input.required<number>();
  rowGap = input.required<number>();
  colGap = input.required<number>();
  dragInProgress = input.required<boolean>();
  widget = input.required<Required<DashboardSidePanelData>['widget']>();
  onDeleteWidget = output<string>();
  id = computed(() => this.widget().id);
  row = computed(() => this.widget().row);
  col = computed(() => this.widget().col);
  rowSpan = computed(() => this.widget().rowSpan);
  colSpan = computed(() => this.widget().colSpan);
  dragStart = output();
  dragEnd = output();
  dragPosition = { x: 0, y: 0 };
  sizeChange = output<{
    row: number;
    col: number;
    rowSpan: number;
    colSpan: number;
  }>();
  dragging = signal(false);
  resizeOrigin = signal<`${'top' | 'bottom'}-${'left' | 'right'}` | null>(null);
  resizing = computed(() => this.resizeOrigin() !== null);
  top = computed(() => {
    return this.rowGap() * this.row() + this.cellHeight() * (this.row() - 1);
  });
  left = computed(() => {
    return this.colGap() * this.col() + this.cellWidth() * (this.col() - 1);
  });
  width = computed(() => {
    return (
      this.cellWidth() * this.colSpan() + (this.colSpan() - 1) * this.colGap()
    );
  });
  height = computed(() => {
    return (
      this.cellHeight() * this.rowSpan() + (this.rowSpan() - 1) * this.rowGap()
    );
  });
  localHeight = signal(0);
  localWidth = signal(0);
  localLeft = signal(0);
  localTop = signal(0);

  currentHeight = computed(() => {
    return this.resizing() ? this.localHeight() : this.height();
  });
  currentWidth = computed(() => {
    return this.resizing() ? this.localWidth() : this.width();
  });
  currentLeft = computed(() => {
    return this.resizing() ? this.localLeft() : this.left();
  });
  currentTop = computed(() => {
    return this.resizing() ? this.localTop() : this.top();
  });
  loading = inject(LOADING);
  private previousEvent: PointerEvent | undefined;
  onDragEnd() {
    this.dragging.set(false);
  }
  onDragStart() {
    this.dragging.set(true);
    this.dragStart.emit();
  }

  onResizeStart(
    e: PointerEvent,
    origin: `${'top' | 'bottom'}-${'left' | 'right'}`
  ) {
    this.resizeOrigin.set(origin);
    this.previousEvent = e;
    this.localHeight.set(this.height());
    this.localWidth.set(this.width());
    this.localLeft.set(this.left());
    this.localTop.set(this.top());
  }

  getSizeChangePreview = ({
    height,
    top,
    width,
    left,
  }: {
    height: number;
    top: number;
    width: number;
    left: number;
  }) => ({
    rowSpan: Math.round(
      (height + this.rowGap()) / (this.cellHeight() + this.rowGap())
    ),
    row: Math.round(
      (top + this.cellHeight()) / (this.cellHeight() + this.rowGap())
    ),
    colSpan: Math.round(
      (width + this.colGap()) / (this.cellWidth() + this.colGap())
    ),
    col: Math.round(
      (left + this.cellWidth()) / (this.cellWidth() + this.colGap())
    ),
  });

  onResizeEnd() {
    if (!this.resizing()) {
      return;
    }
    this.resizeOrigin.set(null);
    this.previousEvent = undefined;
    this.sizeChange.emit(
      this.getSizeChangePreview({
        height: this.localHeight(),
        left: this.localLeft(),
        top: this.localTop(),
        width: this.localWidth(),
      })
    );
  }
  prefix = inject(TRANSLATION_PREFIX);
  constructor(
    private readonly overlay: OverlayService,
    private readonly translate: TranslateService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService
  ) {
    listenForEventsWhen(
      () => this.resizing(),
      document.body,
      'pointerup',
      () => this.onResizeEnd()
    );
    listenForEventsWhen(
      () => this.resizing(),
      document,
      'pointermove',
      (e) => this.updateSize(e)
    );
  }

  resetPosition() {
    this.dragPosition = { x: 0, y: 0 };
  }

  async deleteWidget() {
    try {
      await this.overlay.openAlert({
        message: t(
          this.prefix,
          `Do you want to delete this widget?`,
          this.translate
        ),
        title: t(this.prefix, `Warning`, this.translate),
      });
    } catch (err) {
      return;
    }
    this.loading.set(true);
    this.apollo
      .mutate<DeleteWidgetMutation, DeleteWidgetMutationVariables>({
        mutation: deleteWidgetMutation,
        variables: {
          dashboardId: this.dashboardId(),
          widgetId: this.id(),
        },
      })
      .subscribe({
        next: ({}) => {
          // invalidates dashboard cache
          this.apollo.client.refetchQueries({
            include: ['GetDashboards'],
          });
          this.loading.set(false);
          this.onDeleteWidget.emit(this.id());
          this.toast.notifySuccess(
            t(this.prefix, `Widget deleted`, this.translate)
          );
        },
        error: () => {
          this.loading.set(false);
          this.toast.notifySuccess(
            t(
              this.prefix,
              `An error occurred while deleting the widget`,
              this.translate
            )
          );
        },
      });
  }

  editWidget() {
    this.overlay.openSidePanel(DashboardSidePanelComponent, {
      dashboardId: this.dashboardId(),
      widget: this.widget(),
      mode: 'edit',
      widgets: this.widgets(),
    });
  }
  cloneWidget() {
    this.overlay.openSidePanel(DashboardSidePanelComponent, {
      dashboardId: this.dashboardId(),
      widget: this.widget(),
      mode: 'clone',
      widgets: this.widgets(),
    });
  }

  updateSize(e: PointerEvent) {
    if (!this.resizing() || !this.previousEvent) {
      return;
    }
    const distance = {
      x: this.previousEvent.screenX - e.screenX,
      y: this.previousEvent.screenY - e.screenY,
    };
    this.previousEvent = e;
    let newWidth = this.localWidth();
    let newHeight = this.localHeight();
    let newLeft = this.localLeft();
    let newTop = this.localTop();
    if (
      this.resizeOrigin() === 'bottom-right' ||
      this.resizeOrigin() === 'top-right'
    ) {
      // right side => updating width
      newWidth = this.localWidth() - distance.x;
    } else {
      // left side => updating left AND width
      newLeft = this.localLeft() - distance.x;
      newWidth = this.localWidth() + distance.x;
    }
    if (
      this.resizeOrigin() === 'bottom-right' ||
      this.resizeOrigin() === 'bottom-left'
    ) {
      // bottom => updating height
      newHeight = this.localHeight() - distance.y;
    } else {
      // top => updating top AND height
      newTop = this.localTop() - distance.y;
      newHeight = this.localHeight() + distance.y;
    }
    if (newWidth < this.minWidth) {
      newWidth = this.minWidth;
      if (
        this.resizeOrigin() === 'bottom-left' ||
        this.resizeOrigin() === 'top-left'
      ) {
        newLeft = this.localLeft() + (this.localWidth() - newWidth);
      }
    }
    if (newHeight < this.minHeight) {
      newHeight = this.minHeight;
      if (
        this.resizeOrigin() === 'top-right' ||
        this.resizeOrigin() === 'top-left'
      ) {
        newTop = this.localTop() + (this.localHeight() - newHeight);
      }
    }
    const pos = this.getSizeChangePreview({
      height: newHeight,
      left: newLeft,
      top: newTop,
      width: newWidth,
    });
    if (
      !(pos.col <= 0 || pos.colSpan <= 0 || pos.col + pos.colSpan - 1 > COLS)
    ) {
      this.localLeft.set(newLeft);
      this.localWidth.set(newWidth);
    }
    if (!(pos.row <= 0 || pos.rowSpan <= 0)) {
      this.localTop.set(newTop);
      this.localHeight.set(newHeight);
    }
  }
}
